import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import Processing from 'ext-ui/components/Processing/Processing'
import { spacing } from 'ext-ui/style/spacing'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SelectSubscriptionBanner from 'src/assets/images/SelectSubscriptionBanner.svg'
import Tag, { TagColorSchemes } from 'src/components/Tag/Tag'
import useChargebeeCheckout from 'src/hooks/useChargebeeCheckout'
import { useTranslate } from 'src/localization/Trns'
import { BillingCycle } from 'src/models/organizations.models'
import { closeModal } from 'src/navigation/Lnk'
import { useQueryPricingPlans } from 'src/queries/organizations/pricingPlans/pricingPlans.queries'
import { useQueryOrganizationSubscription } from 'src/queries/organizations/saas.queries'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import styled, { css, CSSProp } from 'styled-components'

const PlanTypeDiv = styled.div<{ blueBorder?: boolean }>`
  min-height: 284px;
  display: flex;
  padding: ${spacing(5)};
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  border-radius: 8px;
  gap: ${spacing(3)};
  border: 1px solid
    ${({ blueBorder }): CSSProp =>
      blueBorder
        ? css`
            ${themeConstants.colors.cta}
          `
        : css`
            ${styleConstants.colors.gray_dark}
          `};
`

interface PlanCardProps {
  title: string
  price: string
  onSelectPlan: () => void
  buttonVariant: 'primary' | 'secondary'
  tag?: string
  blueBorder?: boolean
  showBilledAnnually?: boolean
}

const PlanCard: React.FC<PlanCardProps> = ({
  title,
  price,
  onSelectPlan,
  buttonVariant,
  tag = undefined,
  blueBorder = false,
  showBilledAnnually = false,
}) => {
  const translate = useTranslate()

  const minimumBillingUsers = 10

  const dollarAmount = price.split('.')[0]
  const centsAmount = price.split('.')[1]

  return (
    <PlanTypeDiv blueBorder={blueBorder}>
      <div className={'flex-column gap-3 align-start'}>
        <span
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          {translate(title)}
        </span>
        <div
          css={css`
            height: 20px;
          `}
        >
          {tag && (
            <Tag
              color={TagColorSchemes.Blue}
              text={tag}
              fontSize={12}
              css={css`
                white-space: nowrap;
              `}
            />
          )}
        </div>
        <div
          css={css`
            display: flex;
            align-items: baseline;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 4px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  40,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )}
              `}
            >
              {'$'}
              {dollarAmount}
            </span>
            <span
              css={css`
                display: flex;
                padding: 4px 0px;
                justify-content: center;
                align-items: center;
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )}
              `}
            >{`.${centsAmount}`}</span>
          </div>
          <span
            css={css`
              ${getFontCss(
                16,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate('/user/month')}
            {showBilledAnnually && '*'}
          </span>
        </div>
        <div className={'flex-column gap-1'}>
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate('Minimum billing: ${minimumBillingUsers} users', {
              minimumBillingUsers: minimumBillingUsers.toString(),
            })}
          </span>
          {showBilledAnnually && (
            <span
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate(`*Billed annually`)}
            </span>
          )}
        </div>
      </div>

      <Button
        onClick={onSelectPlan}
        variant={buttonVariant}
        dropShadow={false}
        data-testid={`${title.toLowerCase().replace(/\s+/g, '-')}-button`}
      >
        {translate('Select Plan')}
      </Button>
    </PlanTypeDiv>
  )
}

const UpgradeSubscription = (): React.ReactElement => {
  const translate = useTranslate()
  const orgId =
    useSelector(selectUserExplicitOrganizationListModelFromState)?.id ?? ''

  const { data: pricingPlans, isLoading: pricingPlansLoading } =
    useQueryPricingPlans(orgId)
  const proPlan = pricingPlans?.plans.find((plan) => plan.id.includes('pro'))
  const proPlanPricing = proPlan?.pricing.billingIntervals

  const monthlyPrice =
    ((proPlanPricing?.monthly?.amountCentsPerUser || 0) / 100).toString() ||
    '$0.00'
  const yearlyPrice =
    ((proPlanPricing?.yearly?.amountCentsPerUser || 0) / 12 / 100).toString() ||
    '$0.00'

  const dispatch = useDispatch()

  const { data: organizationSubscription } = useQueryOrganizationSubscription({
    orgId: orgId,
  })

  const { trigger: triggerCheckout, renderCheckout } = useChargebeeCheckout()
  const [isUpgradeOpen, setIsUpgradeOpen] = React.useState(true)

  const handlePlanSelection = (billingCycle: BillingCycle): void => {
    setIsUpgradeOpen(false)
    const isReactivate =
      organizationSubscription?.subscription?.state.status === 'CANCELLED'
    triggerCheckout(
      {
        type: isReactivate ? 'reactivate' : 'create',
        args: { orgId, body: { billingPeriod: billingCycle } },
      },
      {
        flow: isReactivate ? 'reactivate' : 'create',
        entryPoint: 'upgrade',
        dismissible: true,
        onSuccess: (): void => {
          dispatch(closeModal())
        },
        onClose: (): void => setIsUpgradeOpen(true),
      },
    )
  }

  return (
    <>
      <BaseModal
        isOpen={isUpgradeOpen}
        onClose={() => dispatch(closeModal())}
        css={css`
          .BaseModal-Body {
            display: flex;
            padding: 60px;
            flex-direction: column;
            align-items: center;
            gap: 40px;
            border-radius: 10px;
          }
        `}
        aria-label={translate('Upgrade Subscription Modal')}
      >
        <img
          width={'100%'}
          src={SelectSubscriptionBanner}
          alt="Select Subscription Banner"
        />
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            gap: 16px;
            align-self: stretch;
          `}
        >
          {pricingPlansLoading ? (
            <div className={'flex-column align-center w-full'}>
              <Processing />
            </div>
          ) : (
            <>
              <PlanCard
                title="Pro Monthly"
                price={monthlyPrice}
                onSelectPlan={() => handlePlanSelection(BillingCycle.MONTHLY)}
                buttonVariant="secondary"
                blueBorder={false}
                showBilledAnnually={false}
              />
              <PlanCard
                title="Pro Yearly"
                price={yearlyPrice}
                onSelectPlan={() => handlePlanSelection(BillingCycle.YEARLY)}
                buttonVariant="primary"
                tag={proPlan?.yearlyBadge}
                blueBorder
                showBilledAnnually
              />
            </>
          )}
        </div>
      </BaseModal>
      {renderCheckout()}
    </>
  )
}

export default UpgradeSubscription
